import React, { useEffect, useMemo, useState } from 'react';
import {
  WellIngredientDropInfoDetails,
  WellIngredientInfo,
} from 'components/Experiment/constants';
import { RoundValue } from 'utils/Global';
import './WellDropInfoTopBar.scss';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';

export const WELL_DROP_INFO_TOPBAR_SLIDE_MS = 150;

export const COMPACT_MAX_CANVAS_WIDTH = 800;

const WIDE_LAYOUT = {
  root: 'flex-row text-[1rem] leading-[1.125rem]',
  summary: 'shrink-0',
  summaryText: 'whitespace-nowrap',
  ingredients:
    'flex-1 flex-row-reverse flex-wrap items-center justify-start gap-x-2 gap-y-1 leading-[1rem]',
  ingredient: 'min-w-[30%]',
  ingredientText: '',
};

const COMPACT_LAYOUT = {
  root: 'flex-col gap-0.5 px-2 py-1 text-[0.75rem] leading-[1rem]',
  summary: 'w-full',
  summaryText: 'whitespace-nowrap text-ellipsis',
  ingredients: 'w-full flex-col items-start',
  ingredient: 'min-w-0',
  ingredientText: 'whitespace-nowrap text-ellipsis',
};

export type WellDropInfoTopBarProps = {
  wellIngDropInfo: WellIngredientDropInfoDetails;
  isHovered?: boolean;
  isSticky?: boolean;
  canvasWidth?: number;
};

const formatIngredient = (w: WellIngredientInfo) =>
  `${RoundValue(w.conc)} ${w.concUnit} ${w.longName}${
    w.pH ? ` ${RoundValue(w.pH)} pH` : ''
  } (${w.type})`;

const toHexColor = (color?: number): string | undefined => {
  if (color === undefined || color === null) {
    return undefined;
  }
  return `#${(color >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
};

const WellDropInfoTopBar = ({
  wellIngDropInfo,
  isHovered = false,
  isSticky = false,
  canvasWidth,
}: WellDropInfoTopBarProps) => {
  const [isVisible, setIsVisible] = useState(isSticky);

  useEffect(() => {
    setIsVisible(isSticky || isHovered);
  }, [isHovered, isSticky]);

  const { header, volumes, ingredients } = useMemo(() => {
    if (!wellIngDropInfo) {
      return {
        header: '',
        volumes: '',
        ingredients: [] as { label: string; color?: string }[],
      };
    }

    const {
      plateID,
      plateBarcode,
      wellName,
      dropNumber,
      dropWellVolume,
      dropProteinVolume,
      wellIngredients,
    } = wellIngDropInfo;

    return {
      header: `${plateID}, ${plateBarcode}, ${wellName}, ${dropNumber}`,
      volumes: `Drop Well Vol: ${RoundValue(
        dropWellVolume,
      )} μL | Drop Protein Vol: ${RoundValue(dropProteinVolume)} μL`,
      ingredients: (wellIngredients || []).map((w) => ({
        label: formatIngredient(w),
        color: toHexColor(w.ingredientColor),
      })),
    };
  }, [wellIngDropInfo]);

  const title = [header, volumes, ...ingredients.map((i) => i.label)]
    .filter(Boolean)
    .join('\n');

  const isCompact = canvasWidth !== undefined && canvasWidth <= COMPACT_MAX_CANVAS_WIDTH;
  const layout = isCompact ? COMPACT_LAYOUT : WIDE_LAYOUT;

  return (
    <div className="well-drop-info-topbar-clip">
      <div
        id="well-drop-info-topbar"
        className={`well-drop-info-topbar flex ${layout.root}${
          isVisible ? ' well-drop-info-topbar--visible' : ''
        }`}
        title={title}
        aria-hidden={!isVisible}
      >
        <div className={`flex flex-row gap-2 justify-start min-w-0 ${layout.summary}`}>
          <div className="flex-none">
            <RMSvgIcon
              id="editbarcoderesponsemodalbody-error-svg-icon"
              name="Plate"
              fill="#FEFEFE"
              width="1.25rem"
              height="1rem"
            />
          </div>
          <div className={`overflow-hidden min-w-0 ${layout.summaryText}`}>{header}</div>
          <div className={`overflow-hidden min-w-0 ${layout.summaryText}`}>{volumes}</div>
        </div>
        <div className={`flex min-w-0 ${layout.ingredients}`}>
          {ingredients.map((ingredient, index) => [
            index > 0 && !isCompact ? (
              <div
                key={`sep-${index}`}
                className="opacity-70 flex-none"
                aria-hidden="true"
              >
                |
              </div>
            ) : null,
            <div
              key={`ing-${index}`}
              className={`max-w-full flex flex-row items-center ${layout.ingredient}`}
            >
              {ingredient.color && (
                <span
                  className="w-[0.75rem] h-[0.75rem] rounded-full mr-1.5 border border-[#FFFFFF66] flex-none"
                  style={{ backgroundColor: ingredient.color }}
                  aria-hidden="true"
                />
              )}
              <span className={`overflow-hidden min-w-0 ${layout.ingredientText}`}>
                {ingredient.label}
              </span>
            </div>,
          ])}
        </div>
      </div>
    </div>
  );
};

export default WellDropInfoTopBar;
